import { Component, Input, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { Wallet, WalletInfo } from '@model/wallet';
import { WalletService } from '@service/wallet/wallet.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-wallet-display',
  templateUrl: './wallet-display.component.html',
  styleUrls: ['./wallet-display.component.scss']
})
export class WalletDisplayComponent implements OnInit, OnChanges {
  @Input() customerUuid: string;
  @Input() compact: boolean = false;
  @Input() showDetails: boolean = true;
  
  walletInfo: WalletInfo;
  wallet: Wallet;
  loading: boolean = false;
  error: string = null;

  constructor(
    public walletService: WalletService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    if (this.customerUuid) {
      this.loadWallet();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customerUuid && !changes.customerUuid.firstChange) {
      this.loadWallet();
    }
  }

  loadWallet(): void {
    if (!this.customerUuid) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.walletService.getWallet(this.customerUuid).subscribe(
      (response: WalletInfo) => {
        this.walletInfo = response;
        // Transformation des données snake_case en camelCase
        this.wallet = this.mapWalletData(response.wallet);
        console.log('Wallet mappé:', this.wallet); // Pour debug
        this.loading = false;
      },
      error => {
        console.error('Erreur chargement wallet:', error);
        this.error = 'Impossible de charger le wallet';
        this.loading = false;
        this.toastr.error('Erreur lors du chargement du wallet', 'Erreur');
      }
    );
  }

  /**
   * Transforme les données snake_case de l'API en camelCase
   */
  private mapWalletData(rawWallet: any): Wallet {
    return {
      id: rawWallet.id || 0,
      uuid: rawWallet.uuid || '',
      code: rawWallet.code || '',
      solde: rawWallet.solde_actuel || rawWallet.solde || 0,
      detteExigible: rawWallet.dette_exigible || 0,
      detteFuture: rawWallet.dette_future || 0,
      detteTotale: rawWallet.dette_totale || 0,
      creditDisponible: rawWallet.credit_disponible || 0,
      etat: rawWallet.etat || 'EQUILIBRE',
      statutClient: rawWallet.statut_client || rawWallet.statutClient || 'A_JOUR',
      echeancesEnRetard: rawWallet.echeances_en_retard || 0,
      montantEnRetard: rawWallet.montant_en_retard || 0,
      prochaineEcheance: rawWallet.prochaine_echeance || null,
      montantProchaineEcheance: rawWallet.montant_prochaine_echeance || 0,
      messageEtat: rawWallet.message_etat || '',
      peutAcheter: rawWallet.peut_acheter || false,
      niveauRisque: rawWallet.niveau_risque || 'MOYEN',
      dateMiseAJour: rawWallet.date_mise_a_jour || new Date().toISOString()
    };
  }

  recalculateWallet(): void {
    if (!this.customerUuid) {
      return;
    }

    this.loading = true;
    this.walletService.recalculateWallet(this.customerUuid).subscribe(
      (response) => {
        this.toastr.success('Wallet recalculé avec succès', 'Succès');
        this.loadWallet();
      },
      error => {
        console.error('Erreur recalcul wallet:', error);
        this.loading = false;
        this.toastr.error('Erreur lors du recalcul du wallet', 'Erreur');
      }
    );
  }

  getBadgeClass(): string {
    if (!this.wallet) {
      return 'badge-secondary';
    }
    return this.walletService.getStatutBadgeClass(this.wallet.statutClient);
  }

  getRisqueClass(): string {
    if (!this.wallet) {
      return 'text-secondary';
    }
    return this.walletService.getRisqueClass(this.wallet.niveauRisque);
  }

  getStatutIcon(): string {
    if (!this.wallet) {
      return 'fa-question-circle';
    }
    return this.walletService.getStatutIcon(this.wallet.statutClient);
  }

  getSoldeClass(): string {
    if (!this.wallet) {
      return '';
    }
    const solde = this.wallet.solde || 0;
    if (solde > 0) {
      return 'text-success';
    } else if (solde < 0) {
      return 'text-danger';
    }
    return 'text-muted';
  }

  formatMontant(montant: number): string {
    if (montant === null || montant === undefined || isNaN(montant)) {
      montant = 0;
    }
    return this.walletService.formatMontant(montant);
  }

  translateStatut(statut: string): string {
    return this.walletService.translateStatut(statut);
  }

  translateNiveauRisque(niveau: string): string {
    return this.walletService.translateNiveauRisque(niveau);
  }

  hasEcheancesEnRetard(): boolean {
    return this.wallet && (this.wallet.echeancesEnRetard || 0) > 0;
  }

  hasCreditDisponible(): boolean {
    return this.wallet && (this.wallet.creditDisponible || 0) > 0;
  }

  getSoldeIcon(): string {
    if (!this.wallet) {
      return 'fa-wallet';
    }
    const solde = this.wallet.solde || 0;
    if (solde > 0) {
      return 'fa-arrow-up';
    } else if (solde < 0) {
      return 'fa-arrow-down';
    }
    return 'fa-equals';
  }
}